import { Button, Checkbox, Divider, Popover, Radio, Select, Space } from "antd"
import { InfoCard } from "../common/InfoCard"
import { useEffect, useState } from "react"
import { BalancePoint } from "./common"
import { NodeState } from "@/components/FlowSheetEditor/services/modal"
import { ManualCell } from "../common/ManualCell"
import { useUpdateEffect } from "ahooks"
import { SettingOutlined } from "@ant-design/icons"
import {isNil} from 'lodash-es'
import Title from "antd/lib/typography/Title"
import { useGetVariableUnit } from "@/pages/Project/hooks/useDimUnits"

interface TableCardColumn {
  title: string
  key: string
  hideAble: boolean
  shortTitle: string
  dimCode?: string
}

interface TableCardProps {
  cardClassName?: string
  storageKey: string
  unit: NodeState
  data: any
  onChange: (changes) => void
  onBPChange: (changes) => void
}

const storageKey = 'STREAM1_TABLE'

export function TableCard(props: TableCardProps) {
  const { cardClassName, unit, data, onChange, onBPChange} = props
  const [keys, setKeys] = useState<string[]>([])
  const stream1InData = data?.vars?.S1_IN || {}
  const stream1OutData = data?.vars?.S1_OUT || {}
  const stream1BPData = data?.vars?.S1_BP || {}
  const initPoint = unit?.simArgs?.S1_BP
  const [point, setPoint] = useState<null | BalancePoint>(initPoint?? null)
  const initDirection = unit?.simArgs?.FLOW_DIRECTION ?? 'CO_CURRENT'
  const [direction, setDirection] = useState(initDirection)
  const getUnitEntityByCode = useGetVariableUnit()

  useUpdateEffect(() => {
    setPoint(initPoint ?? null)
  }, [initPoint])

  const columns: TableCardColumn[] = [
    {
      title: 'Temperature (T)',
      key: 'T',
      hideAble: true,
      shortTitle: 'T',
      dimCode: 'TEMP',
    },
    {
      title: 'Pressure (P)',
      key: 'P',
      hideAble: true,
      shortTitle: 'P',
      dimCode: 'PRES'
    },
    {
      title: 'Vapor Fraction (VF)',
      key: 'VF',
      hideAble: true,
      shortTitle: 'VF',
      dimCode: 'PF'
    },
    {
      title: 'Enthalpy (h)',
      key: 'h',
      hideAble: true,
      shortTitle: 'h',
      dimCode: 'MOL_H',
    },
  ]

   const renderCell = (col: TableCardColumn, data) => {
    const {key, dimCode} = col;
    const cellData = data[key]
    const unitEntiry = getUnitEntityByCode(dimCode)

    return <ManualCell cellData={cellData} readonly={true} dimCode={dimCode} unitEntiry={unitEntiry}  />
  }

  const renderInlet = (col: TableCardColumn) => {
    return renderCell(col, stream1InData)
  }

  const renderOutlet = (col: TableCardColumn) => {
    return renderCell(col, stream1OutData)
  }

  const renderPoint = (col: TableCardColumn) => {
    return renderCell(col, stream1BPData)
  }

  useEffect(() => {
    const keyStr = localStorage.getItem(storageKey)
    if (keyStr) {
      setKeys(JSON.parse(keyStr))
    } else {
      setKeys(columns.map(item => item.key))
    }
  }, [])

  const onColumnsChange = (values) => {
    setKeys(values)
    localStorage.setItem(storageKey, JSON.stringify(values))
  }

  const columnOptions = columns.map(item => {
    return {
      label: item.title,
      key: item.key,
      value: item.key
    }
  })

  const otherColumns = columns.filter(item => {
    if (!item.hideAble) return false;
    if (keys.length === 0) {
      return true;
    }
    return keys.includes(item.key)
  })

  const setBalancePoint = (val) => {
    const simArgs = {...(unit.simArgs || {})}
    if (val === null) {
      delete simArgs['S1_BP']
      delete simArgs['S2_BP']
      onChange(simArgs)
    } else {
      simArgs.S1_BP = val
      onBPChange(simArgs)
    }
    setPoint(val)
  }

  const onDirectionChange = (e) => {
    const val = e.target.value
    const simArgs = {...(unit.simArgs || {})}
    simArgs.FLOW_DIRECTION = val
    onChange(simArgs)
    setDirection(val)
  }

  const popoverContent = (
    <>
      <Checkbox.Group
        onChange={onColumnsChange}
        value={keys}
        className="d-block"
      >
        {columnOptions.map(option => {
          return <div key={option.key}>
            <Checkbox value={option.value}>{option.label}</Checkbox>
          </div>
        })}
      </Checkbox.Group>
      <Divider className="mt-1 mb-1" />
      <Checkbox checked={!isNil(point)}  onChange={(e) => {
        const isChecked = e.target.checked;
        setBalancePoint(isChecked ? BalancePoint.BoilingPoint : null)
      }}>Insert balance point</Checkbox>
      <Divider className="mt-1 mb-1" />
      <Title level={5}>Flow direction</Title>
      <Radio.Group
        value={direction}
        onChange={onDirectionChange}
        options={[
          { value: 'CO_CURRENT', label: 'Co-Current' },
          { value: 'COUNTER_CURRENT', label: 'Counter-Current' },
        ]}
      />
    </>
    
  )

  return <InfoCard
    title={'Stream1'}
    className={cardClassName}
    action={<Space>
      <Popover content={popoverContent} trigger={'click'} title={<Title level={4}>Stream1 Display columns</Title>}>
        <Button icon={<SettingOutlined />} />
      </Popover>
    </Space>}
  >
    <table style={{width: '100%'}} className="bordered-table break-word-table">
      <thead>
        <tr>
          <th scope="col" style={{width: 140}}>Point</th>
          {otherColumns.map((col) => {
            let title = col.shortTitle
            if (col.dimCode) {
              const unitEntiry = getUnitEntityByCode(col.dimCode)
              if (unitEntiry) {
                title = `${title}[${unitEntiry.unit?.code || '-'}]`
              }
            }
            return <th scope="col" key={col.key}>{title}</th>
          })}
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>Inlet</td>
          {otherColumns.map((col) => {
            return <td scope="col" key={col.key}>{renderInlet(col)}</td>
          })}
        </tr>
        {!isNil(point) && <tr>
          <td>
            <Select value={point} style={{width: '100%'}} onChange={setBalancePoint}>
              <Select.Option value={BalancePoint.Calculated}>Calculated</Select.Option>
              <Select.Option value={BalancePoint.BoilingPoint}>Boiling Point</Select.Option>
              <Select.Option value={BalancePoint.DewPoint}>Dew Point </Select.Option>
            </Select>
          </td>
          {otherColumns.map((col) => {
            return <td scope="col" key={col.key}>{renderPoint(col)}</td>
          })}
        </tr>}
        <tr>
          <td>Outlet</td>
          {otherColumns.map((col) => {
            return <td scope="col" key={col.key}>{renderOutlet(col)}</td>
          })}
        </tr>
      </tbody>
    </table>
  </InfoCard>
}